import {loadScript} from '../utils';
import {getFromStorage} from '../storage';
import {publishExternalEvent} from '../api/externalAppState';

import * as events from './events';

export {events};

const SCRIPT_ELEMENT_CLASS = 'RTA2-loader';

const DIRECT_EVENTS = [events.PAGE_VIEW_EVENT, events.TAB_VIEW_EVENT];
const GENERIC_EVENT_NAME = 'page_event';

interface Metadata {
  [key: string]: string | string[] | number | undefined;
}

interface RTAIds {
  user: string;
  created: number;
  page: string;
  visit: string;
}

interface RTAWindow extends Window {
  // tslint:disable no-any
  RTA?: {
    tedEvent: (
      eventName: string,
      metadata?: {action: string; name: string; value: Metadata | string} | Metadata
    ) => void;
    ids: Promise<RTAIds>;
  };
  appEventData?: any[];
  // tslint:enable no-any
}

interface QueuedEvent {
  eventName: string;
  metadata?: Metadata;
}

let eventQueue: QueuedEvent[] = [];

export const clearEventQueue = () => {
  eventQueue = [];
};

const buildInternalReferrer = (key: string) =>
  [window.location.protocol, '/' + window.location.host, 'click', key].join('/');

const trackRtaHitForAdobe = async () => {
  const win = window as RTAWindow;

  if (!win.RTA) {
    return;
  }

  const {page: rtaPageId, visit: rtaVisitStr} = await win.RTA.ids;
  const rtaVisitId = rtaVisitStr && rtaVisitStr.split('.')[0];

  if (!win.appEventData) {
    win.appEventData = [];
  }

  win.appEventData.push({
    event: 'rtahits',
    rtaPageId,
    rtaVisitId
  });
};

const sendEventToRTA = (eventName: string, metadata?: Metadata) => {
  const RTA = (window as RTAWindow).RTA;

  if (!RTA || !RTA.tedEvent) {
    throw new Error('RTA not loaded');
  }

  if (DIRECT_EVENTS.includes(eventName)) {
    if (eventName === events.PAGE_VIEW_EVENT) {
      if (metadata) {
        metadata.referrer = buildInternalReferrer((metadata.topStoriesKey as string) || 'search');

        if (metadata.query && typeof metadata.query === 'string') {
          metadata.referrer += '?q=' + encodeURIComponent(metadata.query);
          metadata.tags = metadata.query.trim().split(/\s+/);
        }

        if (metadata.subchannel && typeof metadata.subchannel === 'string') {
          publishExternalEvent(metadata.subchannel);
        }
      } else {
        // tslint:disable-next-line: no-console
        console.warn('Cannot track click to unknown destination');

        return;
      }
    } else if (eventName === events.TAB_VIEW_EVENT) {
      if (metadata && metadata.previousTabKey && metadata.previousTabKey !== metadata.channel) {
        metadata.referrer = buildInternalReferrer(metadata.previousTabKey as string);
      }
      if (metadata && metadata.query && typeof metadata.query === 'string') {
        metadata.tags = metadata.query.trim().split(/\s+/);
      }

      trackRtaHitForAdobe().catch();
    }

    if (metadata) {
      let isDesktopRender = true;

      if ('matchMedia' in window) {
        isDesktopRender = window.matchMedia('(min-width: 768px)').matches;
      }

      if (getFromStorage('mobile-view') === 'DESKTOP') {
        isDesktopRender = true;
      }

      metadata.rp = isDesktopRender ? 'desktop' : 'mobile';
    }

    RTA.tedEvent(eventName, metadata);
  } else {
    RTA.tedEvent(GENERIC_EVENT_NAME, {
      action: 'store',
      name: eventName,
      value: metadata || 'event'
    });

    publishExternalEvent(eventName);
  }
};

let adobeEventCount = 0;

export const clearAdobeCount = () => {
  adobeEventCount = 0;
};

const sendEventToAdobe = (eventName: string, metadata?: Metadata) => {
  const win = window as RTAWindow;

  if (!win.appEventData) {
    win.appEventData = [];
  }

  if (eventName === events.TAB_VIEW_EVENT) {
    adobeEventCount++;

    if (metadata && !metadata.query) {
      return win.appEventData.push({
        event: adobeEventCount <= 1 ? 'Page Loaded' : 'Top Story Tab Change',
        page: {
          pageInfo: {
            pageId: `homepage ${metadata.tab}`,
            pageName: `homepage ${metadata.tab}`,
            pageType: 'homepage',
            selectedGeo: metadata.geo
          }
        }
      });
    }

    if (metadata && metadata.query) {
      return win.appEventData.push({
        event: adobeEventCount <= 1 ? 'Page Loaded' : 'Search',
        page: {
          pageInfo: {
            pageId: 'search',
            pageName: 'search',
            pageType: 'search',
            searchKeyword: metadata.query
          }
        }
      });
    }

    return;
  }

  return;
};

export const trackEvent = (eventName: string, metadata?: Metadata) => {
  const RTA = (window as RTAWindow).RTA;

  if (RTA && RTA.tedEvent) {
    sendEventToRTA(eventName, metadata);
  } else {
    eventQueue.push({eventName, metadata});
  }

  sendEventToAdobe(eventName, metadata);
};

export const initTracking = async ({hosts, script}: {hosts: string; script: string}) => {
  try {
    await loadScript(script, {
      class: SCRIPT_ELEMENT_CLASS,
      'data-hosts': hosts
    });

    const RTA = (window as RTAWindow).RTA;

    if (!RTA || !RTA.tedEvent) {
      throw new Error('Error loading RTA');
    }

    for (const queuedEvent of eventQueue) {
      sendEventToRTA(queuedEvent.eventName, queuedEvent.metadata);
    }

    clearEventQueue();
  } catch (error) {
    // TODO: log error
  }
};
